import React, { useEffect, useState } from "react";
import {
    Button,
    Form,
    Header,
    Icon,
    Input,
    Label,
    Loader,
    Message,
    Modal,
    ModalActions,
    ModalContent,
    Pagination,
    Table,
} from "semantic-ui-react";
import { toast } from "react-toastify";
import { getDataQuality } from "../../../services/reporting.service";
import {
    DataQualityIssue,
    DataQualityResponse,
    ReportFilters,
    SortDirection,
} from "../../../types/models/Reporting";
import {
    formatCurrency,
    formatReportDate,
    orderStatusColor,
    orderStatusLabel,
} from "../reportingFormatters";

interface Props {
    open: boolean;
    issue?: DataQualityIssue;
    filters: ReportFilters;
    onClose: () => void;
}

const DataQualityModal = ({ open, issue, filters, onClose }: Props) => {
    const [data, setData] = useState<DataQualityResponse>();
    const [loading, setLoading] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [size] = useState(10);
    const [sortProperty, setSortProperty] = useState("createdAt");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

    useEffect(() => {
        if (!open || !issue) return;
        let active = true;
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await getDataQuality(
                    issue,
                    { ...filters, search: search || filters.search },
                    { page, size, sort: `${sortProperty},${sortDirection}` }
                );
                if (active) setData(response.data);
            } catch (error: any) {
                if (active) toast.error(error.message || "No fue posible consultar la incidencia.");
            } finally {
                if (active) setLoading(false);
            }
        };
        fetchData();
        return () => { active = false; };
    }, [open, issue, filters, page, size, search, sortProperty, sortDirection]);

    useEffect(() => {
        if (!open) return;
        setPage(0);
        setSearchInput("");
        setSearch("");
        setSortProperty("createdAt");
        setSortDirection("desc");
    }, [open, issue]);

    const changeSort = (property: string) => {
        setPage(0);
        setSortDirection((current) => sortProperty === property && current === "asc" ? "desc" : "asc");
        setSortProperty(property);
    };

    const sorted = (property: string) => sortProperty === property
        ? (sortDirection === "asc" ? "ascending" : "descending")
        : undefined;

    return (
        <Modal
            closeIcon
            closeOnDimmerClick
            size="fullscreen"
            open={open}
            onClose={onClose}
            className="dashboard-fullscreen-modal dashboard-data-quality-modal"
        >
            <Header icon="database" content={data?.label || "Detalle de calidad de información"} />
            <ModalContent scrolling>
                <div className="quality-modal-toolbar">
                    <div>
                        <span className="dashboard-eyebrow">Registros detectados</span>
                        <strong>{data?.totalElements ?? 0} pedidos requieren corrección</strong>
                        <p>Se muestran referencias administrativas; abre el pedido para corregir su información.</p>
                    </div>
                    <Form onSubmit={() => { setPage(0); setSearch(searchInput.trim()); }}>
                        <Input
                            action={{ icon: "search", type: "submit", content: "Buscar" }}
                            value={searchInput}
                            onChange={(_, input) => setSearchInput(String(input.value))}
                            placeholder="Factura, cliente, SKU o producto"
                        />
                    </Form>
                </div>

                {loading && <Loader active inline="centered">Consultando réplica...</Loader>}
                {!loading && data?.orders.length === 0 && (
                    <Message info icon="check circle" header="Sin incidencias" content="No existen registros con este criterio." />
                )}

                {!loading && Boolean(data?.orders.length) && (
                    <div className="dashboard-table-scroll">
                        <Table sortable striped compact selectable className="dashboard-table">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell sorted={sorted("invoice")} onClick={() => changeSort("invoice")}>Factura</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("createdAt")} onClick={() => changeSort("createdAt")}>Creación</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("clientName")} onClick={() => changeSort("clientName")}>Cliente</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("advisorName")} onClick={() => changeSort("advisorName")}>Asesor</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("productName")} onClick={() => changeSort("productName")}>Producto</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("status")} onClick={() => changeSort("status")}>Estado</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("totalAmount")} onClick={() => changeSort("totalAmount")} textAlign="right">Importe</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("missingFields")} onClick={() => changeSort("missingFields")}>Información faltante</Table.HeaderCell>
                                    <Table.HeaderCell />
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {data?.orders.map((order) => (
                                    <Table.Row key={order.id}>
                                        <Table.Cell><strong>#{order.invoice || "—"}</strong><small>{order.id}</small></Table.Cell>
                                        <Table.Cell>{formatReportDate(order.createdAt, true)}</Table.Cell>
                                        <Table.Cell>{order.clientName || <Label basic color="red">Sin cliente</Label>}</Table.Cell>
                                        <Table.Cell>{order.advisorName || "Sin asignar"}</Table.Cell>
                                        <Table.Cell><strong>{order.productName || "Sin producto"}</strong><small>{order.productSku || "Sin SKU"}</small></Table.Cell>
                                        <Table.Cell>
                                            <Label color={orderStatusColor(order.status)}>
                                                {orderStatusLabel(order.status, order.statusLabel)}
                                            </Label>
                                        </Table.Cell>
                                        <Table.Cell textAlign="right">{formatCurrency(order.totalAmount)}</Table.Cell>
                                        <Table.Cell>
                                            <div className="dashboard-label-list">
                                                {order.missingFields.map((field) => <Label basic color="red" key={field}>{field}</Label>)}
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell collapsing>
                                            <Button
                                                as="a"
                                                secondary
                                                icon="search"
                                                size="mini"
                                                href={`/orders/${order.id}/details`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            />
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </div>
                )}

                {(data?.totalPages || 0) > 1 && (
                    <div className="dashboard-pagination">
                        <Pagination
                            activePage={page + 1}
                            totalPages={data?.totalPages || 1}
                            onPageChange={(_, pagination) => setPage(Number(pagination.activePage) - 1)}
                        />
                    </div>
                )}
            </ModalContent>
            <ModalActions>
                <Button onClick={onClose}><Icon name="cancel" /> Cerrar</Button>
            </ModalActions>
        </Modal>
    );
};

export default DataQualityModal;
